import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Globe, Palette, Layout, Eye, Save, Loader2, Copy, Check, ExternalLink,
  Image, Type, Settings2, ChevronUp, ChevronDown, Plus, Trash2, Instagram,
  MapPin, Star, Calendar, Sparkles
} from 'lucide-react';

const FONTS = [
  { v: 'inter', l: 'Inter (moderno)' }, { v: 'poppins', l: 'Poppins (amigable)' },
  { v: 'playfair', l: 'Playfair Display (elegante)' }, { v: 'lato', l: 'Lato (limpio)' },
];

const HERO_STYLES = [
  { v: 'gradient', l: 'Gradiente de color' }, { v: 'image', l: 'Imagen de fondo' },
  { v: 'split', l: 'Dividido' }, { v: 'minimal', l: 'Minimalista' },
];

const SECTION_META = {
  hero: { l: 'Hero / Portada', icon: Image, desc: 'La primera sección que verá el visitante' },
  booking: { l: 'Formulario de reserva', icon: Calendar, desc: 'Widget de reserva embebido (fijo)' },
  about: { l: 'Sobre nosotros', icon: Type, desc: 'Descripción del restaurante' },
  events: { l: 'Eventos', icon: Sparkles, desc: 'Próximos eventos del restaurante' },
  experiences: { l: 'Experiencias', icon: Star, desc: 'Experiencias gastronómicas disponibles' },
  map: { l: 'Mapa y ubicación', icon: MapPin, desc: 'Mapa interactivo con la ubicación' },
  reviews: { l: 'Reseñas', icon: Star, desc: 'Valoraciones de Google y TripAdvisor' },
  instagram: { l: 'Instagram', icon: Instagram, desc: 'Feed de Instagram del restaurante' },
};

const ALL_SECTIONS = Object.keys(SECTION_META);

function LivePreview({ config, restaurant }) {
  const primaryColor = config.primaryColor || '#F97316';
  const fontMap = { inter: 'Inter', poppins: 'Poppins', playfair: 'Playfair Display', lato: 'Lato' };
  const font = fontMap[config.fontFamily] || 'Inter';

  const heroBg = config.heroStyle === 'gradient'
    ? `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}05)`
    : config.heroStyle === 'minimal' ? '#fff' : `linear-gradient(135deg, ${primaryColor}15, #f8fafc)`;

  return (
    <div className="w-full bg-white rounded-2xl overflow-hidden shadow-xl border border-slate-200" style={{ fontFamily: font, minHeight: 500 }}>
      {/* Mini browser chrome */}
      <div className="bg-slate-100 px-4 py-2.5 flex items-center gap-2 border-b">
        <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400" /><div className="w-3 h-3 rounded-full bg-amber-400" /><div className="w-3 h-3 rounded-full bg-green-400" /></div>
        <div className="flex-1 bg-white rounded-md px-3 py-1 text-[11px] text-slate-400 font-mono ml-2">
          okreserva.com/{config.slug || 'mi-restaurante'}
        </div>
      </div>

      {/* Nav */}
      <div className="px-8 py-3 flex items-center justify-between border-b border-slate-100">
        <span className="font-bold text-sm" style={{ color: primaryColor }}>{restaurant?.name || 'Restaurante'}</span>
        <button className="text-xs px-3 py-1.5 rounded-full text-white font-semibold" style={{ backgroundColor: primaryColor }}>Reservar</button>
      </div>

      {/* Sections preview */}
      {(config.sections || []).map(sec => {
        if (!SECTION_META[sec]) return null;
        return (
          <div key={sec} className="border-b border-slate-50 last:border-0">
            {sec === 'hero' && (
              <div className="px-8 py-10 text-center" style={{ background: heroBg }}>
                <h1 className="text-xl font-black text-slate-800 mb-2 leading-tight">{config.heroTitle || 'Bienvenido a nuestro restaurante'}</h1>
                <p className="text-slate-500 text-xs mb-5 max-w-xs mx-auto">{config.heroSubtitle || 'Reserva tu mesa y vive una experiencia única'}</p>
                <button className="text-xs px-5 py-2 rounded-full text-white font-bold shadow-sm" style={{ backgroundColor: primaryColor }}>
                  Hacer una reserva
                </button>
              </div>
            )}
            {sec === 'booking' && (
              <div className="px-8 py-6 bg-slate-50">
                <p className="text-[10px] uppercase tracking-wider font-bold text-center mb-4" style={{ color: primaryColor }}>Reserva tu mesa</p>
                <div className="bg-white rounded-xl p-4 shadow-sm grid grid-cols-3 gap-2">
                  {['Fecha', 'Hora', 'Personas'].map(f => (
                    <div key={f} className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-[9px] text-slate-400 mb-1">{f}</p>
                      <p className="text-xs font-medium text-slate-600">—</p>
                    </div>
                  ))}
                  <div className="col-span-3 mt-1">
                    <div className="h-7 rounded-lg w-full text-center text-xs text-white font-bold flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                      Buscar disponibilidad
                    </div>
                  </div>
                </div>
              </div>
            )}
            {sec === 'about' && config.aboutText && (
              <div className="px-8 py-6">
                <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: primaryColor }}>Sobre nosotros</p>
                <p className="text-xs text-slate-500 leading-relaxed line-clamp-3">{config.aboutText}</p>
              </div>
            )}
            {sec === 'events' && (
              <div className="px-8 py-4 bg-slate-50">
                <p className="text-[10px] uppercase tracking-wider font-bold mb-3" style={{ color: primaryColor }}>Próximos eventos</p>
                <div className="grid grid-cols-2 gap-2">
                  {[1, 2].map(n => (
                    <div key={n} className="bg-white rounded-lg p-3 shadow-sm text-[10px]">
                      <div className="h-10 rounded bg-slate-100 mb-2" />
                      <p className="font-semibold text-slate-600">Evento de ejemplo {n}</p>
                      <p className="text-slate-400">Próximamente</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sec === 'experiences' && (
              <div className="px-8 py-4">
                <p className="text-[10px] uppercase tracking-wider font-bold mb-3" style={{ color: primaryColor }}>Experiencias</p>
                <div className="grid grid-cols-2 gap-2">
                  {[1, 2].map(n => (
                    <div key={n} className="rounded-lg p-3 border border-slate-100 text-[10px]">
                      <p className="font-semibold text-slate-600">Experiencia {n}</p>
                      <p className="text-slate-400">Desde €35/persona</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sec === 'map' && (
              <div className="px-8 py-4 bg-slate-50">
                <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: primaryColor }}>Dónde estamos</p>
                <div className="h-16 bg-slate-200 rounded-xl flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-slate-400" />
                </div>
              </div>
            )}
            {sec === 'reviews' && (
              <div className="px-8 py-4">
                <p className="text-[10px] uppercase tracking-wider font-bold mb-3" style={{ color: primaryColor }}>Reseñas</p>
                <div className="flex gap-1 items-center mb-2">
                  {[1,2,3,4,5].map(s => <Star key={s} className="h-4 w-4" style={{ fill: primaryColor, color: primaryColor }} />)}
                  <span className="text-xs font-bold text-slate-700 ml-1">4.8</span>
                </div>
                <p className="text-[10px] text-slate-400">Basado en 128 reseñas de Google</p>
              </div>
            )}
            {sec === 'instagram' && (
              <div className="px-8 py-4 bg-slate-50">
                <p className="text-[10px] uppercase tracking-wider font-bold mb-3" style={{ color: primaryColor }}>Instagram</p>
                <div className="grid grid-cols-4 gap-1">
                  {[1,2,3,4].map(n => <div key={n} className="aspect-square bg-slate-200 rounded" />)}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function BookingPage() {
  const [config, setConfig] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [panel, setPanel] = useState('design');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cR, rR] = await Promise.all([api.get('/booking-page/config'), api.get('/restaurant')]);
      setConfig(cR.data);
      setRestaurant(rR.data);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try { await api.patch('/booking-page/config', config); toast.success('Cambios guardados'); }
    catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const togglePublish = async () => {
    setPublishing(true);
    try {
      const newConfig = { ...config, published: !config.published };
      await api.patch('/booking-page/config', newConfig);
      setConfig(newConfig);
      toast.success(newConfig.published ? '¡Página publicada!' : 'Página despublicada');
    } catch { toast.error('Error'); }
    finally { setPublishing(false); }
  };

  const copyUrl = () => {
    const url = `${window.location.origin}/b/${config.slug || 'mi-restaurante'}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('URL copiada');
  };

  const moveSection = (i, dir) => {
    const secs = [...(config.sections || [])];
    if (dir === 'up' && i > 0) { [secs[i-1], secs[i]] = [secs[i], secs[i-1]]; }
    if (dir === 'down' && i < secs.length - 1) { [secs[i], secs[i+1]] = [secs[i+1], secs[i]]; }
    setConfig(c => ({ ...c, sections: secs }));
  };

  const removeSection = (sec) => {
    if (sec === 'booking') { toast.error('El formulario de reserva no se puede eliminar'); return; }
    setConfig(c => ({ ...c, sections: c.sections.filter(s => s !== sec) }));
  };

  const addSection = (sec) => {
    if (config.sections.includes(sec)) return;
    setConfig(c => ({ ...c, sections: [...c.sections, sec] }));
  };

  if (loading || !config) return (
    <div className="h-screen flex flex-col"><TopBar title="Página de Reservas" helpSection="booking-page" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
    </div>
  );

  const publicUrl = `${window.location.origin}/b/${config.slug || 'mi-restaurante'}`;

  return (
    <div className="h-screen flex flex-col bg-slate-50 overflow-hidden">
      <TopBar title="Página de Reservas Pública" helpSection="booking-page" actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={copyUrl} className="h-8 text-xs">
            {copied ? <Check className="h-3.5 w-3.5 mr-1 text-green-500" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
            Copiar URL
          </Button>
          <Button onClick={save} disabled={saving} variant="outline" className="h-8 text-xs">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Guardar
          </Button>
          <Button onClick={togglePublish} disabled={publishing}
            className={cn("h-8 text-xs", config.published ? "bg-slate-600 hover:bg-slate-700 text-white" : "bg-green-600 hover:bg-green-700 text-white")}>
            {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Globe className="h-3.5 w-3.5 mr-1" />}
            {config.published ? 'Despublicar' : 'Publicar'}
          </Button>
        </div>
      } />

      <div className="flex flex-1 overflow-hidden">
        {/* Left panel */}
        <div className="w-80 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col overflow-hidden">
          {/* Status bar */}
          <div className={cn("px-4 py-2.5 flex items-center gap-2 text-xs border-b", config.published ? "bg-green-50 border-green-200" : "bg-slate-50")}>
            <div className={cn("w-2 h-2 rounded-full", config.published ? "bg-green-500 animate-pulse" : "bg-slate-400")} />
            <span className={cn("font-medium", config.published ? "text-green-700" : "text-slate-500")}>
              {config.published ? 'Publicada y activa' : 'Borrador (no publicada)'}
            </span>
          </div>

          {/* Panel tabs */}
          <div className="flex border-b">
            {[{ k: 'design', l: 'Diseño', icon: Palette }, { k: 'sections', l: 'Secciones', icon: Layout }, { k: 'settings', l: 'Config', icon: Settings2 }].map(t => (
              <button key={t.k} onClick={() => setPanel(t.k)}
                className={cn("flex-1 flex flex-col items-center gap-0.5 py-3 text-[11px] font-medium transition-all border-b-2",
                  panel === t.k ? "border-orange-500 text-orange-600 bg-orange-50/50" : "border-transparent text-slate-500 hover:text-slate-700")}>
                <t.icon className="h-4 w-4" />
                {t.l}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-5 text-sm">
            {/* DESIGN PANEL */}
            {panel === 'design' && (
              <>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Colores</p>
                  <div className="space-y-3">
                    {[{ k: 'primaryColor', l: 'Color principal' }, { k: 'backgroundColor', l: 'Fondo' }].map(f => (
                      <div key={f.k} className="flex items-center justify-between">
                        <Label className="text-xs text-slate-500">{f.l}</Label>
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded border" style={{ backgroundColor: config[f.k] }} />
                          <input type="color" value={config[f.k] || '#F97316'}
                            onChange={e => setConfig(c => ({ ...c, [f.k]: e.target.value }))}
                            className="w-8 h-6 rounded border cursor-pointer p-0.5" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Tipografía</p>
                  <Select value={config.fontFamily || 'inter'} onValueChange={v => setConfig(c => ({ ...c, fontFamily: v }))}>
                    <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>{FONTS.map(f => <SelectItem key={f.v} value={f.v} className="text-xs">{f.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Estilo Hero</p>
                  <div className="grid grid-cols-2 gap-2">
                    {HERO_STYLES.map(s => (
                      <button key={s.v} onClick={() => setConfig(c => ({ ...c, heroStyle: s.v }))}
                        className={cn("py-2 px-2 text-[11px] rounded-xl border text-center transition-all",
                          config.heroStyle === s.v ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:border-slate-300")}>
                        {s.l}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* SECTIONS PANEL */}
            {panel === 'sections' && (
              <>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Secciones activas</p>
                  <div className="space-y-2">
                    {(config.sections || []).map((sec, i) => {
                      const meta = SECTION_META[sec];
                      if (!meta) return null;
                      const Icon = meta.icon;
                      const isFixed = sec === 'booking';
                      return (
                        <div key={sec} className={cn("flex items-center gap-2 p-2.5 rounded-xl border",
                          isFixed ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300")}>
                          <Icon className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-semibold text-slate-700 truncate">{meta.l}</p>
                          </div>
                          {!isFixed && (
                            <div className="flex gap-0.5 flex-shrink-0">
                              <button onClick={() => moveSection(i, 'up')} disabled={i === 0} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronUp className="h-3 w-3 text-slate-500" /></button>
                              <button onClick={() => moveSection(i, 'down')} disabled={i === (config.sections || []).length - 1} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronDown className="h-3 w-3 text-slate-500" /></button>
                              <button onClick={() => removeSection(sec)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="h-3 w-3 text-red-400" /></button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Añadir sección</p>
                  <div className="space-y-1.5">
                    {ALL_SECTIONS.filter(s => !(config.sections || []).includes(s)).map(sec => {
                      const meta = SECTION_META[sec];
                      if (!meta) return null;
                      const Icon = meta.icon;
                      return (
                        <button key={sec} onClick={() => addSection(sec)}
                          className="w-full flex items-center gap-2 p-2 rounded-xl border border-dashed border-slate-300 hover:border-orange-400 text-left group">
                          <Icon className="h-3.5 w-3.5 text-slate-300 group-hover:text-orange-400" />
                          <span className="text-[11px] text-slate-400 group-hover:text-orange-600">{meta.l}</span>
                          <Plus className="h-3 w-3 ml-auto text-slate-300 group-hover:text-orange-400" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* SETTINGS PANEL */}
            {panel === 'settings' && (
              <>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">URL de la página</p>
                  <div className="flex items-center gap-1 text-[11px] bg-slate-50 rounded-xl px-3 py-2 border">
                    <span className="text-slate-400">okreserva.com/</span>
                    <input value={config.slug || ''} onChange={e => setConfig(c => ({ ...c, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                      className="flex-1 bg-transparent outline-none font-medium text-slate-700" placeholder="mi-restaurante" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Título hero</Label>
                  <Input value={config.heroTitle || ''} onChange={e => setConfig(c => ({ ...c, heroTitle: e.target.value }))} className="text-xs" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Subtítulo hero</Label>
                  <Input value={config.heroSubtitle || ''} onChange={e => setConfig(c => ({ ...c, heroSubtitle: e.target.value }))} className="text-xs" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Texto "Sobre nosotros"</Label>
                  <textarea value={config.aboutText || ''} onChange={e => setConfig(c => ({ ...c, aboutText: e.target.value }))}
                    rows={4} className="w-full text-xs border border-input rounded-xl px-3 py-2 resize-none outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Redes sociales</p>
                  {[
                    { k: 'instagram', l: '📷 Instagram (@usuario)' },
                    { k: 'facebook', l: '📘 Facebook (URL)' },
                    { k: 'tripadvisor', l: '🟢 TripAdvisor (URL)' },
                    { k: 'tiktok', l: '🎵 TikTok (@usuario)' },
                    { k: 'twitter', l: '🐦 X / Twitter (@usuario)' },
                    { k: 'googlemaps', l: '🗺️ Google Maps (URL)' },
                  ].map(s => (
                    <div key={s.k} className="mb-2">
                      <Label className="text-[10px] text-slate-400 mb-0.5 block">{s.l}</Label>
                      <Input value={config.socialLinks?.[s.k] || ''} onChange={e => setConfig(c => ({ ...c, socialLinks: { ...c.socialLinks, [s.k]: e.target.value } }))} className="text-xs h-8" />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">WhatsApp</p>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs text-slate-500">Mostrar botón WhatsApp</Label>
                    <input type="checkbox" checked={!!config.whatsappEnabled} onChange={e => setConfig(c => ({ ...c, whatsappEnabled: e.target.checked }))} className="accent-orange-500" />
                  </div>
                  {config.whatsappEnabled && (
                    <div>
                      <Label className="text-[10px] text-slate-400 mb-0.5 block">Número (con código país)</Label>
                      <Input value={config.whatsappNumber || ''} onChange={e => setConfig(c => ({ ...c, whatsappNumber: e.target.value }))} placeholder="+521234567890" className="text-xs h-8" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">SEO</p>
                  <div className="mb-2">
                    <Label className="text-[10px] text-slate-400 mb-0.5 block">Meta título</Label>
                    <Input value={config.seoTitle || ''} onChange={e => setConfig(c => ({ ...c, seoTitle: e.target.value }))} placeholder="Reserva en Mi Restaurante" className="text-xs h-8" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-400 mb-0.5 block">Meta descripción</Label>
                    <textarea value={config.seoDescription || ''} onChange={e => setConfig(c => ({ ...c, seoDescription: e.target.value }))}
                      rows={2} placeholder="Reserva fácilmente en línea..." className="w-full text-xs border border-input rounded-lg px-2 py-1.5 resize-none outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Analytics</p>
                  <div className="mb-2">
                    <Label className="text-[10px] text-slate-400 mb-0.5 block">Google Analytics ID (G-XXXXXXXX)</Label>
                    <Input value={config.gaId || ''} onChange={e => setConfig(c => ({ ...c, gaId: e.target.value }))} placeholder="G-XXXXXXXXXX" className="text-xs h-8 font-mono" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-400 mb-0.5 block">Meta Pixel ID</Label>
                    <Input value={config.metaPixelId || ''} onChange={e => setConfig(c => ({ ...c, metaPixelId: e.target.value }))} placeholder="123456789012345" className="text-xs h-8 font-mono" />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* URL bar */}
          <div className="border-t p-3">
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2 text-xs">
              <Globe className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              <span className="text-slate-500 truncate flex-1">{publicUrl}</span>
              <button onClick={copyUrl}>{copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600" />}</button>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-100">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-medium text-slate-600">Vista previa en vivo</p>
            <div className="flex gap-2">
              <span className={cn("text-xs px-2.5 py-1 rounded-full font-semibold",
                config.published ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600")}>
                {config.published ? '🟢 Publicada' : '⚫ Borrador'}
              </span>
            </div>
          </div>
          <LivePreview config={config} restaurant={restaurant} />
        </div>
      </div>
    </div>
  );
}
